import { useMemo } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";
import { Doughnut, Bar, PolarArea } from "react-chartjs-2";
import { FaExclamationTriangle, FaWrench, FaClock, FaTools } from "react-icons/fa";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler
);

// Color palette for charts - matches alarm colors
const CHART_COLORS = {
  pf: "#f39c12", // Predictive Failure - Orange
  fl: "#e74c3c", // Failure - Red
  mo: "#9b59b6", // Maintenance Overdue - Purple
  mf: "#3498db", // Maintenance Deficiency - Blue
};

// Background colors with opacity
const CHART_BG_COLORS = {
  pf: "rgba(243, 156, 18, 0.15)",
  fl: "rgba(231, 76, 60, 0.15)",
  mo: "rgba(155, 89, 182, 0.15)",
  mf: "rgba(52, 152, 219, 0.15)",
};

/**
 * AlarmDistributionChart - Doughnut chart showing alarm distribution by type
 *
 * @param {object} data - Alarm counts by type
 * @param {number} data.pf_cnt - Predictive Failure count
 * @param {number} data.fl_cnt - Failure count
 * @param {number} data.mo_cnt - Maintenance Overdue count
 * @param {number} data.mf_cnt - Maintenance Deficiency count
 */
export const AlarmDistributionChart = ({ data }) => {
  const chartData = useMemo(
    () => ({
      labels: ["PF", "FL", "MO", "MF"],
      datasets: [
        {
          data: [data?.pf_cnt ?? 0, data?.fl_cnt ?? 0, data?.mo_cnt ?? 0, data?.mf_cnt ?? 0],
          backgroundColor: [CHART_COLORS.pf, CHART_COLORS.fl, CHART_COLORS.mo, CHART_COLORS.mf],
          borderWidth: 0,
        },
      ],
    }),
    [data]
  );

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 12,
          padding: 8,
          font: { size: 11 },
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            const labelMap = { PF: "Predictive Failure", FL: "Failure", MO: "Maintenance Overdue", MF: "Maintenance Deficiency" };
            const fullLabel = labelMap[context.label] || context.label;
            return `${fullLabel}: ${value} (${percentage}%)`;
          },
        },
      },
    },
    cutout: "60%",
  };

  const totalAlarms = (data?.pf_cnt ?? 0) + (data?.fl_cnt ?? 0) + (data?.mo_cnt ?? 0) + (data?.mf_cnt ?? 0);

  return (
    <>
      <h3 className="chart-title">Alarm Distribution</h3>
      <div className="chart-wrapper doughnut-chart" style={{ height: "200px", position: "relative" }}>
        {totalAlarms > 0 ? (
          <>
            <Doughnut data={chartData} options={options} />
            <div className="doughnut-center-label">
              <div className="center-value">{totalAlarms}</div>
              <div className="center-label">Total</div>
            </div>
          </>
        ) : (
          <div className="chart-no-data">No alarms to display</div>
        )}
      </div>
    </>
  );
};

/**
 * TopStationsChart - Horizontal bar chart showing top stations by alarm count
 *
 * @param {object} stationData - Station-wise alarm data object
 */
export const TopStationsChart = ({ stationData }) => {
  const { chartData, hasData } = useMemo(() => {
    if (!stationData || Object.keys(stationData).length === 0) {
      return { chartData: null, hasData: false };
    }

    // Convert to array and calculate totals
    const stationsArray = Object.entries(stationData).map(([name, counts]) => ({
      name,
      pf_cnt: counts.pf_cnt ?? 0,
      fl_cnt: counts.fl_cnt ?? 0,
      mo_cnt: counts.mo_cnt ?? 0,
      mf_cnt: counts.mf_cnt ?? 0,
      total: (counts.pf_cnt ?? 0) + (counts.fl_cnt ?? 0) + (counts.mo_cnt ?? 0) + (counts.mf_cnt ?? 0),
    }));

    // Sort by total and take top 10
    const top10 = stationsArray.filter((s) => s.total > 0).sort((a, b) => b.total - a.total).slice(0, 10);

    if (top10.length === 0) {
      return { chartData: null, hasData: false };
    }

    return {
      chartData: {
        labels: top10.map((s) => s.name),
        datasets: [
          { label: "PF", data: top10.map((s) => s.pf_cnt), backgroundColor: CHART_COLORS.pf },
          { label: "FL", data: top10.map((s) => s.fl_cnt), backgroundColor: CHART_COLORS.fl },
          { label: "MO", data: top10.map((s) => s.mo_cnt), backgroundColor: CHART_COLORS.mo },
          { label: "MF", data: top10.map((s) => s.mf_cnt), backgroundColor: CHART_COLORS.mf },
        ],
      },
      hasData: true,
    };
  }, [stationData]);

  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { boxWidth: 12, padding: 12, font: { size: 11 } },
      },
    },
    scales: {
      x: { stacked: true, beginAtZero: true },
      y: { stacked: true },
    },
  };

  return (
    <>
      <h3 className="chart-title">Top 10 Stations by Alarms</h3>
      <div className="chart-wrapper bar-chart" style={{ height: "220px" }}>
        {hasData ? (
          <Bar data={chartData} options={options} />
        ) : (
          <div className="chart-no-data">No station alarms to display</div>
        )}
      </div>
    </>
  );
};

/**
 * DivisionAlarmsChart - Vertical stacked bar chart showing alarms by division
 *
 * @param {object} divisionData - Division-wise alarm data object
 */
export const DivisionAlarmsChart = ({ divisionData }) => {
  const { chartData, hasData } = useMemo(() => {
    if (!divisionData || Object.keys(divisionData).length === 0) {
      return { chartData: null, hasData: false };
    }

    // Convert to array
    const divisionsArray = Object.entries(divisionData).map(([name, counts]) => ({
      name: counts.div_code ?? name,
      pf_cnt: counts.pf_cnt ?? 0,
      fl_cnt: counts.fl_cnt ?? 0,
      mo_cnt: counts.mo_cnt ?? 0,
      mf_cnt: counts.mf_cnt ?? 0,
    }));

    // Sort alphabetically
    divisionsArray.sort((a, b) => a.name.localeCompare(b.name));

    return {
      chartData: {
        labels: divisionsArray.map((d) => d.name),
        datasets: [
          { label: "PF", data: divisionsArray.map((d) => d.pf_cnt), backgroundColor: CHART_COLORS.pf },
          { label: "FL", data: divisionsArray.map((d) => d.fl_cnt), backgroundColor: CHART_COLORS.fl },
          { label: "MO", data: divisionsArray.map((d) => d.mo_cnt), backgroundColor: CHART_COLORS.mo },
          { label: "MF", data: divisionsArray.map((d) => d.mf_cnt), backgroundColor: CHART_COLORS.mf },
        ],
      },
      hasData: true,
    };
  }, [divisionData]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { boxWidth: 12, padding: 12, font: { size: 11 } },
      },
    },
    scales: {
      x: { stacked: true },
      y: { stacked: true, beginAtZero: true },
    },
  };

  return (
    <>
      <h3 className="chart-title">Alarms by Division</h3>
      <div className="chart-wrapper bar-chart" style={{ height: "200px" }}>
        {hasData ? (
          <Bar data={chartData} options={options} />
        ) : (
          <div className="chart-no-data">No division alarms to display</div>
        )}
      </div>
    </>
  );
};

/**
 * DivisionAlarmGrid - Modern grid view showing division alarms with visual indicators
 * @param {object} divisionData - Division-wise alarm data
 * @param {function} onDivisionClick - Callback when division card is clicked (for filtering)
 * @param {function} onCountClick - Callback when a specific count is clicked (divCode, sevType)
 * @param {string} selectedDivision - Currently selected division code
 */
export const DivisionAlarmGrid = ({ divisionData, onDivisionClick, onCountClick, selectedDivision }) => {
  const divisions = useMemo(() => {
    if (!divisionData || Object.keys(divisionData).length === 0) return [];
    return Object.entries(divisionData).map(([code, data]) => ({
      code: data.div_code ?? code,
      pf: data.pf_cnt ?? 0,
      fl: data.fl_cnt ?? 0,
      mo: data.mo_cnt ?? 0,
      mf: data.mf_cnt ?? 0,
      total: (data.pf_cnt ?? 0) + (data.fl_cnt ?? 0) + (data.mo_cnt ?? 0) + (data.mf_cnt ?? 0),
      assets: data.total_asset_cnt ?? 0,
      raw: data,
    }));
  }, [divisionData]);

  if (divisions.length === 0) {
    return <div className="chart-no-data">No division data available</div>;
  }

  const maxTotal = Math.max(...divisions.map(d => d.total), 1);

  const handleCountClick = (e, divCode, sevType, count) => {
    e.stopPropagation();
    if (count > 0 && onCountClick) {
      onCountClick(divCode, sevType);
    }
  };

  return (
    <div className="alarm-grid-container">
      {divisions.map((div) => {
        const intensity = div.total / maxTotal;
        const isSelected = selectedDivision === div.code;
        return (
          <div
            key={div.code}
            className={`alarm-grid-item ${isSelected ? "selected" : ""} ${div.total > 0 ? "has-alarms" : "healthy"}`}
            onClick={() => onDivisionClick?.(div)}
            style={{
              "--alarm-intensity": intensity,
              borderLeftColor: div.fl > 0 ? CHART_COLORS.fl : div.pf > 0 ? CHART_COLORS.pf : div.mo > 0 ? CHART_COLORS.mo : div.mf > 0 ? CHART_COLORS.mf : "var(--success-main)",
            }}
          >
            <div className="grid-item-header">
              <span className="grid-item-name">{div.code}</span>
              <span className="grid-item-total">{div.total > 0 ? div.total : "✓"}</span>
            </div>
            <div className="grid-item-bars">
              <div
                className={`alarm-mini-bar ${div.pf > 0 ? "clickable" : ""}`}
                style={{ "--bar-color": CHART_COLORS.pf, "--bar-width": `${div.pf > 0 ? Math.max(20, (div.pf / Math.max(div.total, 1)) * 100) : 0}%` }}
                onClick={(e) => handleCountClick(e, div.code, "pf", div.pf)}
              >
                <span className="bar-label">PF</span>
                <span className="bar-value">{div.pf}</span>
              </div>
              <div
                className={`alarm-mini-bar ${div.fl > 0 ? "clickable" : ""}`}
                style={{ "--bar-color": CHART_COLORS.fl, "--bar-width": `${div.fl > 0 ? Math.max(20, (div.fl / Math.max(div.total, 1)) * 100) : 0}%` }}
                onClick={(e) => handleCountClick(e, div.code, "fl", div.fl)}
              >
                <span className="bar-label">FL</span>
                <span className="bar-value">{div.fl}</span>
              </div>
              <div
                className={`alarm-mini-bar ${div.mo > 0 ? "clickable" : ""}`}
                style={{ "--bar-color": CHART_COLORS.mo, "--bar-width": `${div.mo > 0 ? Math.max(20, (div.mo / Math.max(div.total, 1)) * 100) : 0}%` }}
                onClick={(e) => handleCountClick(e, div.code, "mo", div.mo)}
              >
                <span className="bar-label">MO</span>
                <span className="bar-value">{div.mo}</span>
              </div>
              <div
                className={`alarm-mini-bar ${div.mf > 0 ? "clickable" : ""}`}
                style={{ "--bar-color": CHART_COLORS.mf, "--bar-width": `${div.mf > 0 ? Math.max(20, (div.mf / Math.max(div.total, 1)) * 100) : 0}%` }}
                onClick={(e) => handleCountClick(e, div.code, "mf", div.mf)}
              >
                <span className="bar-label">MF</span>
                <span className="bar-value">{div.mf}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * StationAlarmGrid - Modern grid view showing station alarms with compact visual indicators
 * @param {object} stationData - Station-wise alarm data
 * @param {function} onStationClick - Callback when station card is clicked (for filtering)
 * @param {function} onCountClick - Callback when a specific count is clicked (stnCode, sevType)
 * @param {string} selectedStation - Currently selected station code
 */
export const StationAlarmGrid = ({ stationData, onStationClick, onCountClick, selectedStation }) => {
  const stations = useMemo(() => {
    if (!stationData || Object.keys(stationData).length === 0) return [];
    return Object.entries(stationData).map(([code, data]) => ({
      code: data.customer_name ?? code,
      pf: data.pf_cnt ?? 0,
      fl: data.fl_cnt ?? 0,
      mo: data.mo_cnt ?? 0,
      mf: data.mf_cnt ?? 0,
      total: (data.pf_cnt ?? 0) + (data.fl_cnt ?? 0) + (data.mo_cnt ?? 0) + (data.mf_cnt ?? 0),
      assets: data.total_asset_cnt ?? 0,
      raw: data,
    }));
  }, [stationData]);

  if (stations.length === 0) {
    return <div className="chart-no-data">No station data available</div>;
  }

  const handleCountClick = (e, stnCode, sevType, count) => {
    e.stopPropagation();
    if (count > 0 && onCountClick) {
      onCountClick(stnCode, sevType);
    }
  };

  return (
    <div className="station-alarm-grid">
      {stations.map((stn) => {
        const isSelected = selectedStation === stn.code;
        const hasAlarms = stn.total > 0;
        return (
          <div
            key={stn.code}
            className={`station-grid-item ${isSelected ? "selected" : ""} ${hasAlarms ? "has-alarms" : "healthy"}`}
            onClick={() => onStationClick?.(stn)}
          >
            <div className="station-name">{stn.code}</div>
            <div className="station-counts">
              <span
                className={`count-badge pf ${stn.pf > 0 ? "clickable" : ""}`}
                style={{ opacity: stn.pf > 0 ? 1 : 0.3 }}
                onClick={(e) => handleCountClick(e, stn.code, "pf", stn.pf)}
              >
                {stn.pf}
              </span>
              <span
                className={`count-badge fl ${stn.fl > 0 ? "clickable" : ""}`}
                style={{ opacity: stn.fl > 0 ? 1 : 0.3 }}
                onClick={(e) => handleCountClick(e, stn.code, "fl", stn.fl)}
              >
                {stn.fl}
              </span>
              <span
                className={`count-badge mo ${stn.mo > 0 ? "clickable" : ""}`}
                style={{ opacity: stn.mo > 0 ? 1 : 0.3 }}
                onClick={(e) => handleCountClick(e, stn.code, "mo", stn.mo)}
              >
                {stn.mo}
              </span>
              <span
                className={`count-badge mf ${stn.mf > 0 ? "clickable" : ""}`}
                style={{ opacity: stn.mf > 0 ? 1 : 0.3 }}
                onClick={(e) => handleCountClick(e, stn.code, "mf", stn.mf)}
              >
                {stn.mf}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * SystemAlarmsRadialChart - Modern radial/polar chart for system-wise alarms
 */
export const SystemAlarmsRadialChart = ({ systemData, onSystemClick }) => {
  const { chartData, systems, hasData } = useMemo(() => {
    if (!systemData || Object.keys(systemData).length === 0) {
      return { chartData: null, systems: [], hasData: false };
    }

    const systemsArray = Object.entries(systemData).map(([code, data]) => {
      const counts = data.counts ?? {};
      const total = Object.values(counts).reduce((sum, val) => sum + val, 0);
      return {
        code,
        alias: data.software_alias ?? code,
        id: data.software_id,
        total,
      };
    }).filter(s => s.total > 0);

    if (systemsArray.length === 0) {
      return { chartData: null, systems: [], hasData: false };
    }

    // Sort by total descending
    systemsArray.sort((a, b) => b.total - a.total);

    // Generate gradient colors
    const colors = systemsArray.map((_, i) => {
      const hue = (i * 137.5) % 360; // Golden angle for better distribution
      return `hsla(${hue}, 70%, 55%, 0.8)`;
    });

    return {
      chartData: {
        labels: systemsArray.map(s => s.alias || s.code),
        datasets: [{
          data: systemsArray.map(s => s.total),
          backgroundColor: colors,
          borderColor: colors.map(c => c.replace("0.8", "1")),
          borderWidth: 2,
        }],
      },
      systems: systemsArray,
      hasData: true,
    };
  }, [systemData]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // We'll use custom legend
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.label}: ${context.raw} alarms`,
        },
      },
    },
    scales: {
      r: {
        beginAtZero: true,
        ticks: {
          display: false,
        },
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
        },
      },
    },
    onClick: (event, elements) => {
      if (elements.length > 0 && onSystemClick) {
        const index = elements[0].index;
        onSystemClick(systems[index]);
      }
    },
  };

  if (!hasData) {
    return <div className="chart-no-data">No system alarms to display</div>;
  }

  return (
    <div className="system-radial-container">
      <div className="system-chart-wrapper">
        <PolarArea data={chartData} options={options} />
      </div>
      <div className="system-legend">
        {systems.slice(0, 8).map((sys, i) => (
          <div
            key={sys.code}
            className="system-legend-item"
            onClick={() => onSystemClick?.(sys)}
          >
            <span
              className="legend-color"
              style={{ backgroundColor: chartData.datasets[0].backgroundColor[i] }}
            />
            <span className="legend-label">{sys.alias || sys.code}</span>
            <span className="legend-value">{sys.total}</span>
          </div>
        ))}
        {systems.length > 8 && (
          <div className="system-legend-more">+{systems.length - 8} more</div>
        )}
      </div>
    </div>
  );
};

/**
 * AlarmSummaryCards - Modern summary cards with icons and progress indicators
 */
export const AlarmSummaryCards = ({ data, onCardClick }) => {
  const cards = [
    {
      key: "pf",
      label: "Predictive Failure",
      shortLabel: "PF",
      value: data?.pf_cnt ?? 0,
      icon: <FaExclamationTriangle />,
      color: CHART_COLORS.pf,
      bgColor: CHART_BG_COLORS.pf,
    },
    {
      key: "fl",
      label: "Failure",
      shortLabel: "FL",
      value: data?.fl_cnt ?? 0,
      icon: <FaWrench />,
      color: CHART_COLORS.fl,
      bgColor: CHART_BG_COLORS.fl,
    },
    {
      key: "mo",
      label: "Maintenance Overdue",
      shortLabel: "MO",
      value: data?.mo_cnt ?? 0,
      icon: <FaClock />,
      color: CHART_COLORS.mo,
      bgColor: CHART_BG_COLORS.mo,
    },
    {
      key: "mf",
      label: "Maintenance Deficiency",
      shortLabel: "MF",
      value: data?.mf_cnt ?? 0,
      icon: <FaTools />,
      color: CHART_COLORS.mf,
      bgColor: CHART_BG_COLORS.mf,
    },
  ];

  const total = cards.reduce((sum, c) => sum + c.value, 0);
  const maxValue = Math.max(...cards.map(c => c.value), 1);

  return (
    <div className="alarm-summary-cards">
      {cards.map((card) => (
        <div
          key={card.key}
          className="summary-card"
          onClick={() => onCardClick?.(card.key)}
          style={{
            "--card-color": card.color,
            "--card-bg": card.bgColor,
          }}
        >
          <div className="summary-icon">{card.icon}</div>
          <div className="summary-content">
            <div className="summary-value">{card.value}</div>
            <div className="summary-label">{card.shortLabel}</div>
          </div>
          <div
            className="summary-progress"
            style={{
              height: `${total > 0 ? (card.value / maxValue) * 100 : 0}%`,
              backgroundColor: card.color,
            }}
          />
        </div>
      ))}
    </div>
  );
};
